/**
 * Entradas do cadastro de produto (D43, D44, D46, D48–D53):
 * NCM com busca oficial, fabricante (fabricantes + fornecedores), unidade de medida,
 * leitor de código de barras pela câmera e área de arquivo com colar (Ctrl+V) e arrastar.
 */
import { useEffect, useRef, useState, type ClipboardEvent, type DragEvent, type ReactNode } from "react";
import { useCreate, useDataProvider, useGetList, useGetOne, useInput, useNotify } from "react-admin";
import Autocomplete from "@mui/material/Autocomplete";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import CameraIcon from "@mui/icons-material/QrCodeScannerOutlined";
import UploadIcon from "@mui/icons-material/UploadFileOutlined";
import { BrowserMultiFormatReader, type IScannerControls } from "@zxing/browser";
import { formatarNcm, soDigitos } from "@sobral/dominio";
import { normalizarBusca } from "../lib/formato";
import { supabase } from "../lib/supabase";

/* ------------------------------------- NCM ------------------------------------- */

export interface NcmOficial {
  codigo: string; // "8471.60.52"
  descricao: string; // própria do código
  descricao_completa: string; // subposições › item › código
}

/**
 * Busca na tabela NCM oficial carregada no banco (Portal Único Siscomex, D55).
 * Cada palavra digitada precisa aparecer (código, descrição completa ou posição), sem acento.
 */
async function buscarNcm(termo: string): Promise<NcmOficial[]> {
  const palavras = normalizarBusca(termo).replace(/\./g, "").split(/\s+/).filter((p) => p.length > 0);
  if (palavras.length === 0 || palavras.join("").length < 2) return [];
  let consulta = supabase.from("ncm").select("codigo_formatado, descricao, descricao_completa");
  for (const p of palavras) consulta = consulta.like("busca", `%${p}%`);
  const { data, error } = await consulta.order("codigo").limit(50);
  if (error || !data) return [];
  return data.map((n) => ({ codigo: n.codigo_formatado as string, descricao: n.descricao as string, descricao_completa: n.descricao_completa as string }));
}

/** Descrição oficial de um NCM de 8 dígitos (para exibir o código já gravado). */
export async function descricaoNcm(ncm: string): Promise<string | null> {
  if (!/^\d{8}$/.test(ncm)) return null;
  const { data } = await supabase.from("ncm").select("descricao_completa").eq("codigo", ncm).maybeSingle();
  return (data?.descricao_completa as string | undefined) ?? null;
}

/**
 * NCM: digita código ou parte da descrição; a lista oficial aparece para escolha.
 * `aoEscolher` recebe o NCM (8 dígitos) e a descrição oficial.
 */
export function EntradaNcm({ aoEscolher }: { aoEscolher: (ncm: string | null, descricao: string | null) => void }) {
  const { field, fieldState } = useInput({ source: "ncm" });
  const valor = (field.value as string | null) ?? null;
  const [texto, setTexto] = useState("");
  const [opcoes, setOpcoes] = useState<NcmOficial[]>([]);
  const [carregando, setCarregando] = useState(false);
  const [descricaoAtual, setDescricaoAtual] = useState<string | null>(null);

  // Descrição do NCM já gravado.
  useEffect(() => {
    let ativo = true;
    if (valor) void descricaoNcm(valor).then((d) => ativo && setDescricaoAtual(d));
    else setDescricaoAtual(null);
    return () => {
      ativo = false;
    };
  }, [valor]);

  // Busca com pequena espera entre teclas.
  useEffect(() => {
    const termo = texto;
    if (termo.trim().length < 2) {
      setOpcoes([]);
      return;
    }
    let ativo = true;
    setCarregando(true);
    const espera = setTimeout(() => {
      buscarNcm(termo)
        .then((lista) => ativo && setOpcoes(lista))
        .catch(() => ativo && setOpcoes([]))
        .finally(() => ativo && setCarregando(false));
    }, 350);
    return () => {
      ativo = false;
      clearTimeout(espera);
    };
  }, [texto]);

  const selecionado: NcmOficial | null = valor
    ? { codigo: formatarNcm(valor), descricao: descricaoAtual ?? "", descricao_completa: descricaoAtual ?? "" }
    : null;

  return (
    <Autocomplete<NcmOficial, false, false, false>
      value={selecionado}
      options={opcoes}
      loading={carregando}
      filterOptions={(x) => x}
      getOptionLabel={(o) => o.codigo}
      isOptionEqualToValue={(a, b) => a.codigo === b.codigo}
      onInputChange={(_, v, motivo) => {
        if (motivo === "input") setTexto(v);
      }}
      onChange={(_, escolhido) => {
        const ncm = escolhido ? soDigitos(escolhido.codigo) : null;
        field.onChange(ncm);
        setDescricaoAtual(escolhido?.descricao_completa ?? null);
        aoEscolher(ncm, escolhido?.descricao ?? null);
      }}
      noOptionsText={texto.length < 2 ? "Digite o código ou parte da descrição" : "Nenhum NCM encontrado"}
      loadingText="Consultando a tabela NCM…"
      renderOption={(props, o) => (
        <li {...props} key={o.codigo}>
          <Box>
            <Typography variant="body2" fontWeight={700}>
              {o.codigo}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {o.descricao_completa}
            </Typography>
          </Box>
        </li>
      )}
      renderInput={(params) => (
        <TextField
          {...params}
          label="NCM"
          error={Boolean(fieldState.error)}
          helperText={descricaoAtual ?? "Busque pelo código ou pela descrição oficial."}
        />
      )}
    />
  );
}

/* ---------------------------------- Fabricante ---------------------------------- */

interface OpcaoFabricante {
  tipo: "fabricante" | "fornecedor" | "novo";
  id: string;
  nome: string;
}

/**
 * Fabricante: lista de fabricantes já usados + nomes dos fornecedores (o fornecedor pode ser o próprio fabricante).
 * Escolher um fornecedor ou digitar um nome novo cadastra o fabricante, que passa a valer para todos.
 */
export function EntradaFabricante() {
  const { field } = useInput({ source: "fabricante_id" });
  const notify = useNotify();
  const [create] = useCreate();
  const [texto, setTexto] = useState("");
  const filtro = texto.trim() ? { "nome@ilike": texto.trim() } : {};
  const { data: fabricantes = [] } = useGetList<{ id: string; nome: string }>("fabricante", {
    filter: { ativo: true, ...filtro },
    pagination: { page: 1, perPage: 30 },
    sort: { field: "nome", order: "ASC" },
  });
  const { data: fornecedores = [] } = useGetList<{ id: string; nome: string }>("fornecedor", {
    filter: { ativo: true, ...(texto.trim() ? { "busca@ilike": normalizarBusca(texto.trim()) } : {}) },
    pagination: { page: 1, perPage: 15 },
    sort: { field: "nome", order: "ASC" },
  });
  const { data: atual } = useGetOne<{ id: string; nome: string }>(
    "fabricante",
    { id: field.value as string },
    { enabled: Boolean(field.value) },
  );

  const nomesFabricantes = new Set(fabricantes.map((f) => normalizarBusca(f.nome)));
  const opcoes: OpcaoFabricante[] = [
    ...fabricantes.map((f) => ({ tipo: "fabricante" as const, id: f.id, nome: f.nome })),
    ...fornecedores
      .filter((f) => !nomesFabricantes.has(normalizarBusca(f.nome)))
      .map((f) => ({ tipo: "fornecedor" as const, id: f.id, nome: f.nome })),
  ];
  const t = texto.trim();
  if (t && !opcoes.some((o) => normalizarBusca(o.nome) === normalizarBusca(t))) {
    opcoes.push({ tipo: "novo", id: "novo", nome: t });
  }
  const selecionado: OpcaoFabricante | null = atual ? { tipo: "fabricante", id: atual.id, nome: atual.nome } : null;

  async function escolher(opcao: OpcaoFabricante | null) {
    if (!opcao) {
      field.onChange(null);
      return;
    }
    if (opcao.tipo === "fabricante") {
      field.onChange(opcao.id);
      return;
    }
    try {
      const novo = await create("fabricante", { data: { nome: opcao.nome } }, { returnPromise: true });
      field.onChange((novo as { id: string }).id);
      notify(`Fabricante "${opcao.nome}" cadastrado.`, { type: "info" });
    } catch (e) {
      notify(`Não foi possível cadastrar o fabricante: ${(e as Error).message}`, { type: "error" });
    }
  }

  return (
    <Autocomplete<OpcaoFabricante, false, false, false>
      value={selecionado}
      options={opcoes}
      filterOptions={(x) => x}
      groupBy={(o) => (o.tipo === "fabricante" ? "Fabricantes" : o.tipo === "fornecedor" ? "Fornecedores" : "Novo")}
      getOptionLabel={(o) => o.nome}
      isOptionEqualToValue={(a, b) => a.id === b.id}
      onInputChange={(_, v, motivo) => {
        if (motivo === "input" || motivo === "clear") setTexto(v);
      }}
      onChange={(_, o) => void escolher(o)}
      renderOption={(props, o) => (
        <li {...props} key={`${o.tipo}-${o.id}`}>
          {o.tipo === "novo" ? `Cadastrar fabricante "${o.nome}"` : o.nome}
        </li>
      )}
      noOptionsText="Digite o nome do fabricante"
      renderInput={(params) => <TextField {...params} label="Fabricante" />}
    />
  );
}

/* ------------------------------ Unidade de medida ------------------------------ */

interface Unidade {
  id: string;
  sigla: string;
  descricao: string;
}

/** Lista de unidades (sigla); uma sigla nova digitada é cadastrada e fica disponível para todos. */
export function useUnidades() {
  const { data = [] } = useGetList<Unidade>("unidade_medida", {
    filter: { ativo: true },
    pagination: { page: 1, perPage: 500 },
    sort: { field: "sigla", order: "ASC" },
  });
  return data;
}

export function SeletorUnidade({
  valor,
  aoMudar,
  rotulo = "Unidade",
  unidades,
  desabilitado,
  erro,
}: {
  valor: string | null;
  aoMudar: (sigla: string | null) => void;
  rotulo?: string;
  unidades: Unidade[];
  desabilitado?: boolean;
  erro?: string;
}) {
  const notify = useNotify();
  const [create] = useCreate();
  const [texto, setTexto] = useState("");
  const sigla = texto.trim().toUpperCase();
  const opcoes: (Unidade | { id: "nova"; sigla: string; descricao: string })[] = [...unidades];
  if (sigla && /^[A-Z0-9ÇÃÕÁÉÍÓÚÂÊÔ]{1,12}$/.test(sigla) && !unidades.some((u) => u.sigla === sigla)) {
    opcoes.push({ id: "nova", sigla, descricao: `Cadastrar unidade "${sigla}"` });
  }
  const selecionada = unidades.find((u) => u.sigla === valor) ?? (valor ? { id: valor, sigla: valor, descricao: valor } : null);

  async function escolher(o: (typeof opcoes)[number] | null) {
    if (!o) return aoMudar(null);
    if (o.id !== "nova") return aoMudar(o.sigla);
    try {
      await create("unidade_medida", { data: { sigla: o.sigla, descricao: o.sigla } }, { returnPromise: true });
      notify(`Unidade "${o.sigla}" cadastrada. A descrição pode ser ajustada em Parâmetros do sistema.`, { type: "info" });
      aoMudar(o.sigla);
    } catch (e) {
      notify(`Não foi possível cadastrar a unidade: ${(e as Error).message}`, { type: "error" });
    }
  }

  return (
    <Autocomplete
      value={selecionada}
      options={opcoes}
      disabled={desabilitado}
      getOptionLabel={(o) => o.sigla}
      isOptionEqualToValue={(a, b) => a.sigla === b.sigla}
      filterOptions={(lista, estado) => {
        const t = normalizarBusca(estado.inputValue);
        return lista.filter((o) => o.id === "nova" || normalizarBusca(`${o.sigla} ${o.descricao}`).includes(t));
      }}
      onInputChange={(_, v) => setTexto(v)}
      onChange={(_, o) => void escolher(o)}
      renderOption={(props, o) => (
        <li {...props} key={o.id}>
          {o.id === "nova" ? (
            o.descricao
          ) : (
            <>
              <strong>{o.sigla}</strong>&nbsp;— {o.descricao}
            </>
          )}
        </li>
      )}
      renderInput={(params) => <TextField {...params} label={rotulo} required error={Boolean(erro)} helperText={erro} />}
    />
  );
}

/* ------------------------------ Código de barras ------------------------------ */

/** Lê um código de barras pela câmera (celular ou webcam). Requer HTTPS e permissão de câmera. */
export function LeitorCodigoBarras({ aberto, aoFechar, aoLer }: { aberto: boolean; aoFechar: () => void; aoLer: (codigo: string) => void }) {
  const video = useRef<HTMLVideoElement | null>(null);
  const [erro, setErro] = useState<string | null>(null);

  useEffect(() => {
    if (!aberto) return;
    let controles: IScannerControls | null = null;
    let encerrado = false;
    setErro(null);
    const leitor = new BrowserMultiFormatReader();
    // O vídeo é montado pelo diálogo; espera um ciclo para ter o elemento.
    const inicio = setTimeout(() => {
      if (!video.current) return;
      leitor
        .decodeFromConstraints({ video: { facingMode: { ideal: "environment" } } }, video.current, (resultado, _erro, c) => {
          if (resultado && !encerrado) {
            encerrado = true;
            c.stop();
            aoLer(resultado.getText());
          }
        })
        .then((c) => {
          controles = c;
          if (encerrado) c.stop();
        })
        .catch(() => setErro("Não foi possível acessar a câmera. Verifique a permissão do navegador."));
    }, 100);
    return () => {
      encerrado = true;
      clearTimeout(inicio);
      controles?.stop();
    };
  }, [aberto, aoLer]);

  return (
    <Dialog open={aberto} onClose={aoFechar} fullWidth maxWidth="sm">
      <DialogTitle>Ler código de barras</DialogTitle>
      <DialogContent>
        {erro ? (
          <Alert severity="warning">{erro}</Alert>
        ) : (
          <>
            <Box component="video" ref={video} muted playsInline sx={{ width: "100%", borderRadius: 1, bgcolor: "black" }} />
            <Typography variant="caption" color="text.secondary">
              Aponte a câmera para o código de barras. A leitura é automática.
            </Typography>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={aoFechar}>Cancelar</Button>
      </DialogActions>
    </Dialog>
  );
}

/** Campo de código de barras com botão de câmera. Aceita também leitor USB (que "digita" o código). */
export function CampoCodigoBarras({
  valor,
  aoMudar,
  aviso,
}: {
  valor: string;
  aoMudar: (codigo: string) => void;
  aviso?: ReactNode;
}) {
  const [lendo, setLendo] = useState(false);
  return (
    <>
      <TextField
        label="Código de barras"
        value={valor}
        onChange={(e) => aoMudar(e.target.value.trim())}
        fullWidth
        inputProps={{ inputMode: "numeric" }}
        helperText={aviso}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <Tooltip title="Ler pela câmera">
                <IconButton edge="end" onClick={() => setLendo(true)} aria-label="Ler código de barras pela câmera">
                  <CameraIcon />
                </IconButton>
              </Tooltip>
            </InputAdornment>
          ),
        }}
      />
      <LeitorCodigoBarras
        aberto={lendo}
        aoFechar={() => setLendo(false)}
        aoLer={(codigo) => {
          setLendo(false);
          aoMudar(codigo);
        }}
      />
    </>
  );
}

/** Procura outra embalagem com o mesmo código de barras (evita cadastro duplicado). */
export function useCodigoBarrasExistente(codigo: string, produtoAtual?: string) {
  const dataProvider = useDataProvider();
  const [existente, setExistente] = useState<{ produto_id: string; descricao: string; codigo: string } | null>(null);
  useEffect(() => {
    let ativo = true;
    const c = codigo.trim();
    if (c.length < 8) {
      setExistente(null);
      return;
    }
    const espera = setTimeout(() => {
      dataProvider
        .getList("produto_embalagem", {
          filter: { codigo_barras: c },
          pagination: { page: 1, perPage: 1 },
          sort: { field: "id", order: "ASC" },
        })
        .then(async ({ data }) => {
          const achada = data[0] as { produto_id: string } | undefined;
          if (!ativo) return;
          if (!achada || achada.produto_id === produtoAtual) return setExistente(null);
          const { data: p } = await dataProvider.getOne("produto", { id: achada.produto_id });
          if (ativo) setExistente({ produto_id: achada.produto_id, descricao: p.descricao, codigo: p.codigo });
        })
        .catch(() => ativo && setExistente(null));
    }, 400);
    return () => {
      ativo = false;
      clearTimeout(espera);
    };
  }, [codigo, produtoAtual, dataProvider]);
  return existente;
}

/* ----------------------------- Área de arquivo ----------------------------- */

/**
 * Área para enviar arquivo: clicar para escolher, arrastar e soltar, ou clicar e colar (Ctrl+V).
 * `aceitos` são tipos MIME (ex.: image/png).
 */
export function ZonaArquivo({
  aceitos,
  rotulo,
  enviando,
  aoReceber,
  children,
  alturaMinima = 180,
}: {
  alturaMinima?: number;
  aceitos: string[];
  rotulo: string;
  enviando: boolean;
  aoReceber: (arquivo: File) => void;
  children?: ReactNode;
}) {
  const entrada = useRef<HTMLInputElement | null>(null);
  const notify = useNotify();
  const [sobre, setSobre] = useState(false);

  function receber(arquivos: FileList | File[] | null | undefined) {
    const arquivo = arquivos ? Array.from(arquivos)[0] : undefined;
    if (!arquivo) return;
    if (!aceitos.includes(arquivo.type)) {
      notify("Tipo de arquivo não aceito nesta área.", { type: "warning" });
      return;
    }
    aoReceber(arquivo);
  }

  function colar(e: ClipboardEvent<HTMLDivElement>) {
    const arquivos = Array.from(e.clipboardData.items)
      .filter((i) => i.kind === "file")
      .map((i) => i.getAsFile())
      .filter((f): f is File => f !== null);
    if (arquivos.length > 0) {
      e.preventDefault();
      receber(arquivos);
    } else {
      notify("A área de transferência não contém um arquivo ou imagem.", { type: "warning" });
    }
  }

  function soltar(e: DragEvent<HTMLDivElement>) {
    e.preventDefault();
    setSobre(false);
    receber(e.dataTransfer.files);
  }

  return (
    <Box
      tabIndex={0}
      role="button"
      aria-label={rotulo}
      onPaste={colar}
      onDragOver={(e) => {
        e.preventDefault();
        setSobre(true);
      }}
      onDragLeave={() => setSobre(false)}
      onDrop={soltar}
      onClick={() => entrada.current?.click()}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") entrada.current?.click();
      }}
      sx={{
        border: "1px dashed",
        borderColor: sobre ? "secondary.main" : "divider",
        borderRadius: 1,
        minHeight: alturaMinima,
        p: 1.5,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: 1,
        cursor: "pointer",
        outline: "none",
        bgcolor: sobre ? "action.hover" : "background.paper",
        "&:focus-visible, &:focus": { borderColor: "secondary.main", borderStyle: "solid" },
      }}
    >
      {children}
      <Typography variant="caption" color="text.secondary" textAlign="center">
        {enviando ? (
          "Enviando…"
        ) : (
          <>
            <UploadIcon sx={{ fontSize: 16, verticalAlign: "text-bottom", mr: 0.5 }} />
            Clique para escolher, arraste o arquivo ou clique aqui e cole (Ctrl+V)
          </>
        )}
      </Typography>
      <input
        ref={entrada}
        type="file"
        hidden
        accept={aceitos.join(",")}
        onChange={(e) => {
          receber(e.target.files);
          e.target.value = "";
        }}
      />
    </Box>
  );
}
